import { useEffect, useRef, useState } from "react";

type RevealProps = {
  children: React.ReactNode;
  delay?: number;
  className?: string;
};

const Reveal = ({ children, delay = 0, className = "" }: RevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let done = false;

    const reveal = () => {
      if (!done) {
        done = true;
        setShown(true);
        cleanup();
      }
    };

    const inView = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh * 0.9 && r.bottom > 0;
    };

    const onScroll = () => { if (inView()) reveal(); };

    const io = "IntersectionObserver" in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((e) => { if (e.isIntersecting) reveal(); });
        }, { threshold: 0.12 })
      : null;

    let timer: ReturnType<typeof setTimeout>;

    function cleanup() {
      if (io) io.disconnect();
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    }

    if (inView()) { reveal(); return cleanup; }
    if (io) io.observe(el);
    window.addEventListener("scroll", onScroll, { passive: true });
    timer = setTimeout(reveal, 1700);
    return cleanup;
  }, []);

  return (
    <div
      ref={ref}
      className={"reveal " + (shown ? "in " : "") + className}
      style={{ transitionDelay: delay + "ms" }}
    >
      {children}
    </div>
  );
};

export default Reveal;
